import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Importing custom components
import { IconButton } from '../../core/icon-button';
import { Tooltip } from '../../core/tooltip';
// Importing hooks
import { useDuration } from '../../../hooks/use-duration';
import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
// Importing icons
import BackwardsIcons from '@mui/icons-material/Replay10Rounded';
import ForwardsIcon from '@mui/icons-material/Forward10Rounded';
import LoadingIcon from '@mui/icons-material/CachedRounded';
import PauseIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import PlayIcon from '@mui/icons-material/PlayCircleOutlineRounded';
// Importing store
import { usePlayerStore } from '../../../store/player.store';
// Importing 3rd party components
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
// ----------------------------------------------------------------------
export const PlayControls = () => {
    // Adding hooks
    const { calculateProgress } = useDuration();
    const { t } = useTranslation();
    // Adding data handler
    const { buffering, duration, played, player, playing, setPlayed, togglePlaying, toggleSeeking } = usePlayerStore(useShallow((state) => ({
        buffering: state.buffering,
        duration: state.duration,
        played: state.played,
        player: state.player,
        playing: state.playing,
        setPlayed: state.setPlayed,
        togglePlaying: state.togglePlaying,
        toggleSeeking: state.toggleSeeking,
    })));
    // Adding callback to handle progress changes
    const handleProgressChange = (event) => {
        var _a;
        // Updating state of seeking to true
        // We do this so the player does not run into issues when updating the playing progress
        toggleSeeking();
        // Calculating new progress value
        const updatedProgress = calculateProgress(event.currentTarget.id, duration, played);
        // Updating state & changing player position
        setPlayed(updatedProgress);
        (_a = player.current) === null || _a === void 0 ? void 0 : _a.seekTo(updatedProgress, 'fraction');
        // Updating state of seeking to false
        toggleSeeking();
    };
    return (_jsxs(Stack, { alignItems: "center", direction: "row", justifyContent: "center", children: [_jsxs(IconButton, { "aria-label": "play", onClick: togglePlaying, children: [!playing && (_jsx(Tooltip, { title: t('player:controls:play:title'), children: _jsx(PlayIcon, { fontSize: "large" }) })), playing && buffering && (_jsx(Tooltip, { title: t('player:controls:buffering:title'), children: _jsx(LoadingIcon, { fontSize: "large", sx: {
                                animationDuration: '2s',
                                animationIterationCount: 'infinite',
                                animationName: 'rotateLoadingIcon',
                                '@keyframes rotateLoadingIcon': {
                                    '0%': { transform: 'rotate(360deg)' },
                                    '100%': { transform: 'rotate(-360deg)' },
                                },
                            } }) })), playing && !buffering && (_jsx(Tooltip, { title: t('player:controls:pause:title'), children: _jsx(PauseIcon, { fontSize: "large" }) }))] }), _jsxs(Box, { display: { xs: 'none', sm: 'flex' }, children: [_jsx(Tooltip, { title: t('player:controls:backwards:title'), children: _jsx(IconButton, { "aria-label": "backwards", id: "backwards", onClick: handleProgressChange, children: _jsx(BackwardsIcons, {}) }) }), _jsx(Tooltip, { title: t('player:controls:forwards:title'), children: _jsx(IconButton, { "aria-label": "forwards", id: "forwards", onClick: handleProgressChange, children: _jsx(ForwardsIcon, {}) }) })] })] }));
};
